import { useEffect, useRef, useState } from 'react';
import { Button, notification, Space } from 'antd';
import { LockOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { isSupported } from 'firebase/messaging';
import { registerPushToken } from 'src/store/auth/reducer';
import { requestToken } from 'src/utils/firebase';
import apiRoutes from 'src/utils/apiRoutes';
import apiRequests from 'src/utils/api';
import { RootState } from 'src/store';
import { loadState, saveState } from 'src/utils/localStorage';

const PushNotification = () => {
  const [token, setToken] = useState<string | null>(null);
  const [isFcmSupported, setIsFcmSupported] = useState(false);
  const isTokenRequestedRef = useRef(false);
  const isPermissionRequestedRef = useRef(false);
  const isSupportedRequestedRef = useRef(false);
  const [api, contextHolder] = notification.useNotification();
  const dispatch = useDispatch();
  const { user, loading } = useSelector((store: RootState) => store.auth);

  const pushNotifications = user?.preferences?.push_notifications;

  const { uuid, push_tokens: pushTokens } = user ?? {};

  if (!isSupportedRequestedRef.current) {
    isSupportedRequestedRef.current = true;

    isSupported()
      .then((value) => setIsFcmSupported(value))
      .catch(() => {});
  }

  if (
    isFcmSupported &&
    !token &&
    !isPermissionRequestedRef.current &&
    Notification.permission === 'granted'
  ) {
    isPermissionRequestedRef.current = true;

    requestToken().then((value) => {
      setToken(value);
    });
  }

  useEffect(() => {
    const userTokens = pushTokens ?? [];

    if (token && uuid && !userTokens.find((item) => item.token === token)) {
      dispatch(registerPushToken(token));
    }
  }, [dispatch, token, uuid, pushTokens]);

  useEffect(() => {
    if (!uuid && token && !loading) {
      apiRequests.delete(`${apiRoutes.FCM_TOKEN}/${token}`);
    }
  }, [loading, token, uuid]);

  useEffect(() => {
    if (pushNotifications === '0') {
      api.destroy('request_notification_permission');
    }
  }, [api, pushNotifications]);

  useEffect(() => {
    if (
      isFcmSupported &&
      !token &&
      Notification.permission === 'default' &&
      pushNotifications === '1' &&
      loadState('showNotificationPermission', '1') !== '0'
    ) {
      api.open({
        key: 'request_notification_permission',
        icon: <LockOutlined style={{ color: '#c4bc35', fontSize: 28 }} />,
        message: 'Notification permission',
        description: (
          <Space size={12} direction="vertical">
            <p>
              Allow our alerts so you can stay up to date with all your tasks
              and projects.
            </p>
            <div className="w-full space-x-2 text-right">
              <Button
                type="link"
                onClick={() => {
                  api.destroy('request_notification_permission');
                  saveState('showNotificationPermission', '0');
                }}
              >
                No thanks
              </Button>
              <Button
                type="primary"
                onClick={async () => {
                  await Notification.requestPermission();

                  if (!isTokenRequestedRef.current) {
                    isTokenRequestedRef.current = true;

                    api.destroy('request_notification_permission');

                    const newToken = await requestToken();

                    setToken(newToken);
                  }
                }}
              >
                Allow
              </Button>
            </div>
          </Space>
        ),
        duration: 0,
        closeIcon: false,
      });
    }
  }, [api, isFcmSupported, token, pushNotifications]);

  return contextHolder;
};

export default PushNotification;
